(ns practitest.frontend.components.grid.fields
  (:require [practitest.frontend.components.grid.common :as gc]
            [practitest.frontend.components.users :as users]
            [practitest.frontend.components.run-status :as run-status]
            [practitest.frontend.components.test-type :as test-type]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.components.grid.filters :as filters]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components.datepicker :as datepicker]
            [practitest.frontend.components.datepicker2 :as datepicker2]
            [tick.core :as tick]
            [clojure.string :as str]
            [practitest.frontend.components.number-input :as number-input]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components.lists :as lists]
            [practitest.frontend.components.spinners :as spinners]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.requirements.testing-coverage :refer [testing-scope-labels]]
            [practitest.frontend.global-fields-settings.common :refer [get-field-format-label]]
            [practitest.frontend.forms3.entity-form.fields :as entity-form-fields]))

(def simple-header gc/header-title)

(def unassigned-user
  {:id nil
   :name (get-translation "generic.label.unassigned" "Unassigned")
   :type "user"
   :avatar {:type "unassigned"}})

(defn simple-text
  [{:keys [field] :as column-info} row]
  (str (get row field)))

(defn simple-cell
  [{:keys [field] :as column-info} row]
  (str (get row field)))

;; Simple fields
(doseq [type [:field :text :date :linkedlist
                  ;; We need to register it here, because our new grid uses
                  ;; old grid filters, so we need to support it as well
                  :external_id_or_display_id]]
 (gc/register-field-type!
  type
  simple-header
  simple-text
  simple-cell))

(gc/register-field-type!
 :number
 simple-header
 (fn [{:keys [field] :as column-info} row]
    ;; Return number itself for correct sorting behaviour
   (get row field))
 simple-cell)

(gc/register-field-type!
 :internal-url
 simple-header
 (fn [{:keys [field]} row]
   (let [value (get row field)]
     (if (map? value)
       (:title value)
       (str value))))
 simple-cell)

(gc/register-field-type!
 :nc-name
 simple-header
 simple-text
 simple-cell)

;; Special field to handle sorting of Display ID (e.g. 4:20)
(gc/register-field-type!
 :display_id
 simple-header
 (fn [{:keys [field] :as column-info} row]
   (let [value (str (get row field))]
      ;; Return number itself for correct sorting behaviour
     (if-let [re-pair (re-find #"^(\d+):(\d+)$" value)]
        ;; Fallback to column value in case of errors
       (fu/try-parse-number2 (nth re-pair 2) value)
       value)))
 simple-cell)

;; column-info structure here:
;
;{
; ;; Mix of predefined and server-sent props:
;
; :default-width 200
; :width         200
; :field         :___f_6
; :type          :list
; :header        "Operating System"
; :show?         true
; :sortable?     true
; :status-colors {"CANCELED" "#ffd900",
;                 "N/A" "#3a87ad",
;                 "BLOCKED" "#c09853"}
; :multiple?     true
; :autofilter?   true
;
; :users         list of users (where applicable)
; :groups        list of groups (where applicable)
;
; ;; Populated by body-cell component
; :container-ref (atom ref-node)
; :editable?     false
; :editing?      false
; :saving?       false
; :editing-value "value"
; :on-edit-change (fn [newvalue])
; :on-edit-save   (fn [newvalue])
; :on-edit-stop   (fn [])
; }

(gc/register-field-type!
 :list
 simple-header
 simple-text
 (fn [{:keys [field possible-values container-ref
               ;; Editing props
              editable? editing? saving? editing-value on-edit-change on-edit-save on-edit-stop] :as column-info}
      row]
   (let [row-value (get row field)
         value (or editing-value row-value)]

     (if (and editable?
              editing?)
       (if saving?
         [spinners/bootstrap-spinner-border]
         [lists/simple-list {:options             possible-values

                              ;; Open dropdown as soon as editing is enabled
                             :autofocus?          true
                             :open-menu-on-focus? true

                              ;; Save on blur
                             :on-blur             (fn [e]
                                                    (if (not= value row-value)
                                                       ;; Only trigger if value changed
                                                      (when on-edit-save
                                                        (on-edit-save value))
                                                       ;; Stop editing
                                                      (when on-edit-stop
                                                        (on-edit-stop))))

                              ;; Override portal target. Otherwise react-select popup does not appear inside table
                             :menu-portal-target  (and container-ref
                                                       (dom/get-root-node @container-ref))

                             :value               value
                             :on-change           (fn [newvalue]
                                                    (when on-edit-change
                                                      (on-edit-change newvalue)))}])
       (str value)))))

(gc/register-field-type!
 :multilist
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (let [values (get row field)]
     (str/join "; " values))))

(gc/register-field-type!
 :date-time
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (fu/formatted-date-time (get row field))))

(defn- date-range-view [picker-component extra-picker-props]
  (fn [{:keys [value on-change]}]
    (let [{:keys [from to]} value]
      [:div
       [:label (get-translation "generic.label.from" "From")]
       [picker-component (merge extra-picker-props
                                {:class     "form-control"
                                 :on-change (fn [e] (when on-change (on-change {:from (when e (str e)) :to to})))
                                 :value     from})]
       [:label (get-translation "generic.label.to" "To")]
       [picker-component (merge extra-picker-props
                                {:class     "form-control"
                                 :on-change (fn [e] (when on-change (on-change {:from from :to (when e (str e))})))
                                 :value     to})]
       (when (and from to (try (tick/< to from) (catch js/Error _ false)))
         [:div.validation-error-message
          (get-translation "grid.date-filter.from-after-to" "From is bigger than To - nothing will be displayed")])])))

(defn- date-range-filtering-fn [parse-fn]
  (fn [filter-state column-info row]
    (let [{:keys [from to]} filter-state
          coerce (fn [v] (when v (if (string? v) (try (parse-fn v) (catch js/Error _ nil)) v)))
          from   (coerce from)
          to     (coerce to)
          cell   (get row (:field column-info))]
      (when (some? cell)
        (let [cell-val (try (parse-fn cell) (catch js/Error _ nil))]
          (when cell-val
            (apply tick/< (remove nil? [from cell-val to]))))))))

(defn- date-range-description-fn [format-fn]
  (fn [filter-state]
    (let [{:keys [from to]} filter-state]
      (str (or (format-fn from) "∞")
           " - "
           (or (format-fn to) "∞")))))

(filters/register-filter!
 :date-time
 (date-range-view datepicker/instant-picker {})
 (date-range-filtering-fn tick/instant)
 (date-range-description-fn fu/formatted-date-time))

(filters/register-filter!
 :date
 (date-range-view datepicker2/date-picker-input {:time? false})
 (date-range-filtering-fn tick/date)
 (date-range-description-fn fu/formatted-local-date))

;; Custom field implementation
(gc/register-field-type!
 :custom
  ;; Header
 (fn [{:keys [header-fn header]
       :as   column-info}]
   (or header
       (header-fn column-info)))
  ;; Text
 (fn [_ _]
    ;; No need to filter/sort here
   nil)
  ;; Cell
 (fn [{:keys [cell-fn] :as column-info} row]
   (cell-fn column-info row)))

;;
;; Complex fields
;;

;; User field
(gc/register-field-type!
 :user
 gc/header-title
 (fn [{:keys [field] :as column-info} row]
   (:name (get row field)))
 (fn [{:keys [field users groups container-ref
               ;; Editing props
              editable? editing? saving? editing-value on-edit-change on-edit-save on-edit-stop]
       :as column-info} row]

    ;; We can have either user map with full information
    ;; or just user_id passed to us - then we should look for user in
   (let [users (concat users
                       groups)
         user-cell (get row field)
         row-user  (if (map? user-cell)
                     user-cell
                     (users/find-user users user-cell))
         value-user (or editing-value row-user)]

     (if (and editable?
              editing?)
       (if saving?
         [spinners/bootstrap-spinner-border]
         [users/user-selector {:users-list          users

                                ;; Open dropdown as soon as editing is enabled
                               :autofocus?          true
                               :open-menu-on-focus? true

                                ;; Save on blur
                               :on-blur             (fn [e]
                                                      (if (not= value-user row-user)
                                                         ;; Only trigger if value changed
                                                        (when on-edit-save
                                                          (on-edit-save value-user))
                                                         ;; Stop editing
                                                        (when on-edit-stop
                                                          (on-edit-stop))))

                                ;; Override portal target. Otherwise react-select popup does not appear inside table
                               :menu-portal-target  (and container-ref
                                                         (dom/get-root-node @container-ref))

                               :user                value-user
                               :on-change           (fn [newvalue]
                                                      (when on-edit-change
                                                        (on-edit-change newvalue)))}])
       [:<>
        [users/user-avatar row-user]
        (:name row-user)]))))

;; users filter
(filters/register-filter!
 :user
 (fn [{:keys [value
              on-apply-filter
              on-change

              column

              get-field-props]}]
   (let [field       (:field column)
          ;; TODO: seems really convoluted - think about something better
         field-props (when get-field-props
                       @(get-field-props field))
         users-list  (concat (when (or (= field :assigned_to)
                                       (= field :test/assigned_to)
                                       (= field :tester)) [unassigned-user])
                             (filter #(#{"user" "group"} (:type %)) (:possible-values field-props)))]
     [filters/multiple-list-filter-base {:value     value
                                         :on-change on-change
                                         :options   users-list

                                         :id-fn     (fn [user]
                                                      (str "user-" (:id user)))

                                         :search-fn (fn [user filter]
                                                      (fu/substring-search? (:name user) filter))

                                         :label-fn  (fn [id user]
                                                      [overflow/overflow-with-tooltip
                                                       (fn [props]
                                                         [:span.selector-current-item
                                                          (assoc props :for id)
                                                          (case (:type user)
                                                            "group" [entity-form-fields/group-avatar user]
                                                            "user"  [users/user-avatar2 user]
                                                            [users/user-avatar2 user])
                                                          [:span.selector-item-text (:name user)]])

                                                       (:name user)])}]))

  ;; Filtering
 (fn [filter-state column-info row]
   (let [users-and-groups filter-state
         cell  (get row (:field column-info))
         cell-id (if (map? cell) (:id cell) cell)
         cell-type (if (map? cell) (:type cell) nil)]
     (some (fn [user-or-group]
             (if (= "group" (:type user-or-group))
               ;; For groups, match if:
               ;; 1. The cell itself is assigned to this group, OR
               ;; 2. The cell's user ID is in the group's user_ids
               (or (and (= "group" cell-type) (= (:id user-or-group) cell-id))
                   (some #(= % cell-id) (:user_ids user-or-group)))
               ;; For users, check direct ID match
               (= (:id user-or-group) cell-id)))
           users-and-groups)))

  ;; Label
 (fn [filter-state]
   (str/join
    ", "
    (map :name filter-state))))


;; Backstory for this strange code
;; 1. Initially `:url` field type was implemented as a map with :title and :url keys and worked as such correctly
;; 2. During work on new grids `:url` field was (I would say erroneously) changed to take title and url from row itself (which is strange)
;;    BTW - we had to introduce :custom-field-url to actually fix an issue with incorrect URL columns display due to this
;; 3. As we're reusing old filters implementation for new grids we have this situation when different cell types have different implementation
;;
;; I decided to put this workaround and in case of string value use it for filtering directly to fix one particular issue quickly
;; Correct fix would be to change :url field type implementation back to title + url object but that would require more changes
;; TODO: cleanup this
(defn- url-get-text [{:keys [field] :as column-info} row]
  (let [cell (get row field)]
    (if (map? cell)
      (:title (get row field))
      cell)))

;; URL field type
;; Autofilter is the same as usual text for now
(doseq [type [:url :custom-field-url]]
  ;; Same backstory as for the `url-get-text` - had to add support for :custom-field-url here as well so it will be filtered correctly
  (gc/register-field-type!
    type
    simple-header
    url-get-text
    (fn [{:keys [field] :as column-info} row]
      (let [{:keys [url title]} (get row field)]
        [:a {:href url} title]))))

;; Run Status
(gc/register-field-type!
 :run_status
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (let [status (get row field)]
     [run-status/status-label status])))

(filters/register-filter!
  :run_status
  (fn [{:keys [value on-change column]}]
    (let [lastrun-field? (= "lastrun" (namespace (:field column)))
          options (if lastrun-field?
                    (cons "" (keys run-status/statuses-map))
                    (keys run-status/statuses-map))]
      [filters/multiple-list-filter-base
       {:value     value
        :on-change on-change
        :options   options
        :label-fn  (fn [id status]
                     (let [is-empty? (empty? status)]
                       [:label.me-4.test-type
                        {:for   id
                         :style (when-not is-empty?
                                  {:color (run-status/get-status-color status)})}
                        (when-not is-empty?
                          [:span.me-2 (run-status/get-status-icon status)])
                        (if is-empty?
                          (str "( " (get-translation "grid.fast-filters.no-value" "No Value") " )")
                          (run-status/get-status-label status))]))}]))

  ;; Filtering
 filters/list-filtering-fn

 (fn [filter-state]
   [:<>
    (map (fn [run-status]
           ^{:key run-status}
            ;; Same as Test type for filter status span
           [:span.ms-2
            [tooltip/hover-tooltip
             [:<>
              [:span.me-2
               (run-status/get-status-icon run-status)]
              (run-status/get-status-label run-status)]
             (run-status/get-status-icon run-status)]])
         filter-state)]))

;; Test Type
(gc/register-field-type!
 :test_type
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (let [type (get row field)]
     [test-type/type-label type])))

(filters/register-filter!
 :test_type
 (fn [{:keys [value
              on-change]}]
   [filters/multiple-list-filter-base {:value     value
                                       :on-change on-change
                                       :options   (keys test-type/types-map)
                                       :label-fn  (fn [id type]
                                                    [:label.me-4.test-type {:for id}
                                                     [:span.me-2 (test-type/get-type-icon type)]
                                                     (test-type/get-type-label type)])}])

  ;; Filtering
 filters/list-filtering-fn

 (fn [filter-state]
   [:<>
    (map (fn [test-type]
           ^{:key test-type}
           [:span.ms-2
            [test-type/type-label test-type]])
         filter-state)]))

(filters/register-filter!
 :testing-scope
 (fn [{:keys [value
              on-change]}]
   [filters/multiple-list-filter-base {:value     value
                                       :on-change on-change
                                       :options   [:LAST_RUN :TESTSETS]
                                       :label-fn  (fn [id scope]
                                                    [:label.me-4.test-type {:for id}
                                                     [:span.me-2 (get (testing-scope-labels) scope)]])}])

  ;; Filtering
 filters/testing-scope-filtering-fn

 (fn [filter-state]
   [:<>
    (map (fn [testing-scope-type]
           ^{:key testing-scope-type}
           [:span.ms-2
            (get (testing-scope-labels) testing-scope-type)])
         filter-state)]))

;; linked entities
(gc/register-field-type!
 :linked-entities
 simple-header
 simple-text
 (fn [{:keys [field] :as _column-info} row]
   (let [entities (get row field)
         links  (for [{:keys [id text url highlight external]} entities]
                  (with-meta
                    (if url
                      [:a.nowrap
                       {:href   url
                        :class  (when highlight "fw-bold")
                        :target (when external "_BLANK")}
                       text]
                      [:span.nowrap
                       {:class (when highlight "fw-bold")}
                       text])
                    {:key id}))]
     [:<>
      (interpose "," links)])))

;; parameters
(gc/register-field-type!
 :parameters
 simple-header
 simple-text
 simple-cell)

;; duration
(gc/register-field-type!
 :duration
 simple-header
 (fn [{:keys [field] :as column-info} row]
   (let [value (or (get row field) 0)]
      ;; Duration is exported as simple integer right now
      ;; TODO: add string support if required ?
     value))
 (fn [{:keys [field] :as column-info} row]
   (let [value (get row field)]
     (fu/format-duration (fu/parse-duration value)))))

;; Tags
(gc/register-field-type!
 :tags
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (let [tags (get row field)]
     (str/join ", " (map #(get-in % [:tag :name]) tags)))))

;; Value score
;; TODO: special filter for this one ?
(gc/register-field-type!
 :value_score
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (let [{:keys [raw normalized score_data] :as vs} (get row field)]
     (if (and (= raw 0) (empty? score_data))
       [:span "NA"]
       [:span {:class (cond
                        (< normalized 20)
                        "text-danger"

                        (< normalized 50)
                        "text-warning"

                        :else
                        "text-success")}
        (fu/fixed-point normalized 2)]))))

(defn- check-both-fields
  [{:keys [from to]
    :as   val}]
  (if (and (nil? from)
           (nil? to))
    nil
    val))

(defn- number-filter-view
  [{:keys [value
           on-change]}]
  (let [{:keys [from to]} value]
    [:div
     [:label (get-translation "generic.label.from" "From")]
     [number-input/number-input
      {:on-change  (fn [e]
                     (when on-change
                       (on-change (check-both-fields {:from e
                                                      :to   to}))))
       :value      from
       :allow-nil? true
       :autofocus? true}]
     [:label (get-translation "generic.label.to" "To")]
     [number-input/number-input
      {:on-change  (fn [e]
                     (when on-change
                       (on-change (check-both-fields {:from from
                                                      :to   e}))))
       :value      to
       :allow-nil? true}]

     (when (and from to
                (> from to))
       [:div.validation-error-message
        "From is bigger than To - nothing will be displayed"])]))

(defn- number-filter-label
  [filter-state]
  (let [{:keys [from to]} filter-state]
    (cond
      (and from to)
      (str "Between " from " and " to)

      from
      (str "≥ " from)

      to
      (str "≤ " to))))

(filters/register-filter!
 :number
 number-filter-view

  ;; Filtering
 (fn [filter-state column-info row]
   (let [{:keys [from to]} filter-state
         cell (get row (:field column-info))]
     (when cell
       (apply <= (remove nil? [from cell to])))))

 number-filter-label)

;; TODO: check this filter logic ?
;; Maybe something with correlation to value score text colors ?
;; or a mix of number filter plus predefined values via quick buttons ?
(filters/register-filter!
 :value_score
 number-filter-view

  ;; Filtering
 (fn [filter-state column-info row]
   (let [{:keys [from to]} filter-state
         {:keys [normalized]} (get row (:field column-info))]
     (when normalized
       (apply <= (remove nil? [from normalized to])))))

 number-filter-label)

;;; steps_status_bar

(gc/register-field-type!
 :status-bar
 simple-header
 (constantly nil)
 (fn [{:keys [field status-colors]} row]
   (let [status-bar (get row field)
         sum        (reduce + (map last status-bar))]
     [tooltip/hover-tooltip
      (if (empty? status-bar)
        "no steps"
        (str/join "; " (map (partial str/join ":") status-bar)))
      (into [:div.progress]
            (for [[status cnt] status-bar]
              [:div.progress-bar
               {:style {:width            (str (int (* 100 (/ cnt sum))) "%")
                        :background-color (status-colors status)}}]))])))

;; Field Type

(gc/register-field-type!
  :global_field_type
  simple-header
  simple-text
  (fn [{:keys [field] :as _} row]
      (let [field-type (get row field)]
           (get-field-format-label field-type))))